import { evaluateCondition } from '@dynamic/ui';

import { Condition, MenuItem } from '#/client';
import { NavigationType } from '#/service/constants';
import { destinationRoutes } from '#/service/routes';
import { Dictionary, safeUrl } from '#/service/utils';

export const getNavigationTarget = (
  item: MenuItem,
  defaultSelectedItemId?: string
): NavigationTarget => {
  const { destinationId, destinationType } = getMenuItemInformation(item);

  const isHome = item?.id === defaultSelectedItemId;

  const pageUrl = isHome ? '/' : `/page/${destinationId}`;

  switch (destinationType as NavigationType) {
    case NavigationType.Page:
      return {
        internalUrl: pageUrl,
        exact: pageUrl === '/' ? true : undefined,
      };
    case NavigationType.Internal:
      return {
        // we need handle 'registration' route separately
        internalUrl:
          destinationRoutes[destinationId] ?? `/${destinationId.toLowerCase()}`,
      };
    case NavigationType.External:
      return {
        externalUrl: safeUrl(destinationId),
      };
    default:
      return {};
  }
};

interface NavigationTarget {
  internalUrl?: string;
  exact?: boolean;
  externalUrl?: string;
}

export const getDefaultMenuItem = (menuItems: MenuItem[]) => {
  return menuItems?.[0];
};

export const getDefaultMenuItemId = (menuItems: MenuItem[]) => {
  return getDefaultMenuItem(menuItems)?.id;
};

export const getMenuItemInformation = (menuItem?: MenuItem) => {
  const {
    display: {
      icon = null,
      type: displayType = '',
      title = '',
      buttonVariant = undefined,
    } = {},
    action: {
      params: {
        destination: {
          id: destinationId = '',
          type: destinationType = '',
        } = {},
      } = {},
    } = {},
  } = menuItem ?? {};

  return {
    destinationId,
    destinationType,
    displayType,
    title,
    icon,
    buttonVariant,
  };
};

export const findMenuItemByDestinationId = (
  items: MenuItem[],
  id: string
): MenuItem | undefined => {
  return items.find((el) => el.action?.params?.destination?.id === id);
};

export const getVisibleItems = (context: object, menuItems?: MenuItem[]) => {
  return (menuItems ?? []).filter((item) => {
    const { visibility } = item;

    return !visibility || evaluateCondition(visibility, context);
  });
};

export const getConditionalRoutes = (
  defaultMenuItemId: string,
  mainMenuItems?: MenuItem[],
  linkMenuItems?: MenuItem[]
): Dictionary<string, Condition> => {
  const conditionalRoutes: Dictionary<string, Condition> = {};

  [...(mainMenuItems ?? []), ...(linkMenuItems ?? [])].forEach((item) => {
    if (!item.visibility) {
      return conditionalRoutes;
    }

    const { internalUrl } = getNavigationTarget(item, defaultMenuItemId);

    if (internalUrl) {
      conditionalRoutes[internalUrl] = item.visibility;
    }
  });

  return conditionalRoutes;
};
